/*
  c-motion: the small JS half of assets/c-motion.css (ClickUp 123y1w8nw54).
  - Splits .c-motion-scrub text into word spans (only where scroll timelines run).
  - Marquees: clones the track until one copy covers the container, then doubles it so the
    -50% loop is seamless. Clones are aria-hidden and inert.
  - Fallback for browsers without scroll timelines: html.c-motion-io + IntersectionObserver
    adds .is-inview once, so the reveal classes still fade/slide in.
  - .c-motion-load-group gets .is-loaded after the entrance, so blocks the theme re-renders
    (price, size picker...) do not replay their entrance.
  - [data-c-motion-play]: "view" adds .is-playing once the element scrolls into view (a timed
    entrance then runs in CSS), "load" plays straight away; both get .is-played afterwards so a
    block the theme re-renders never replays it.
  - One page progress bar.
  Nothing runs under prefers-reduced-motion: reduce.
*/
(() => {
  const html = document.documentElement;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  html.classList.add('c-motion-ready');
  const hasTimeline = Boolean(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));
  const REVEAL = '.c-motion-up, .c-motion-left, .c-motion-right, .c-motion-scale, .c-motion-clip, .c-motion-close, .c-motion-lit, .c-motion-stagger, .c-motion-stamp, .c-motion-split';

  const splitWords = (el) => {
    if (el.dataset.cMotionSplit) return;
    el.dataset.cMotionSplit = 'true';
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      if (!node.nodeValue.trim()) return;
      const fragment = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
        } else {
          const span = document.createElement('span');
          span.className = 'c-motion-word';
          span.textContent = part;
          fragment.appendChild(span);
        }
      });
      node.parentNode.replaceChild(fragment, node);
    });
  };

  const markClone = (node) => {
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('inert', '');
    node.querySelectorAll('[id]').forEach((child) => child.removeAttribute('id'));
  };

  const marquee = (el) => {
    if (el.classList.contains('is-running')) return;
    const track = el.firstElementChild;
    if (!track) return;
    const originals = Array.from(track.children);
    if (!originals.length) return;

    const setWidth = track.scrollWidth;
    if (!setWidth) return;
    const copies = Math.max(1, Math.ceil(el.clientWidth / setWidth));
    for (let i = 1; i < copies; i += 1) {
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        markClone(clone);
        track.appendChild(clone);
      });
    }
    Array.from(track.children).forEach((item) => {
      const clone = item.cloneNode(true);
      markClone(clone);
      track.appendChild(clone);
    });

    // Speed stays constant however many copies were needed.
    const base = parseFloat(el.dataset.cMotionDuration) || 40;
    el.style.setProperty('--c-motion-marquee-duration', `${base * copies}s`);
    el.classList.add('is-running');
  };

  let observer = null;
  const fallback = (scope) => {
    if (!('IntersectionObserver' in window)) return;
    html.classList.add('c-motion-io');
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px' }
      );
    }
    scope.querySelectorAll(REVEAL).forEach((el) => {
      if (el.matches('.c-motion-stagger, .c-motion-stamp, .c-motion-split')) {
        Array.from(el.children).forEach((child, index) => child.style.setProperty('--c-motion-i', index));
      }
      observer.observe(el);
    });
  };

  const PLAY_MS = 2200;
  const play = (el) => {
    if (el.dataset.cMotionPlayReady) return;
    el.dataset.cMotionPlayReady = 'true';
    const start = () => {
      el.classList.add('is-playing');
      window.setTimeout(() => el.classList.add('is-played'), PLAY_MS);
    };
    if (el.dataset.cMotionPlay !== 'view' || !('IntersectionObserver' in window)) {
      start();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        start();
      },
      { rootMargin: '0px 0px -20% 0px' }
    );
    io.observe(el);
  };

  const progressBar = () => {
    if (!hasTimeline || document.querySelector('.c-motion-progress')) return;
    const bar = document.createElement('div');
    bar.className = 'c-motion-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
  };

  const init = (scope) => {
    if (hasTimeline) {
      scope.querySelectorAll('.c-motion-scrub').forEach(splitWords);
    } else {
      fallback(scope);
    }

    scope.querySelectorAll('[data-c-motion-marquee]').forEach(marquee);
    scope.querySelectorAll('[data-c-motion-play]').forEach(play);

    scope.querySelectorAll('.c-motion-load-group:not(.is-loaded)').forEach((group) => {
      window.setTimeout(() => group.classList.add('is-loaded'), 2000);
    });
  };

  const start = () => {
    init(document);
    progressBar();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
